import React from "react";

import { faUsers } from "@fortawesome/pro-solid-svg-icons/faUsers";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Popover from "antd/es/popover";
import Tooltip from "antd/es/tooltip";
import { FormattedMessage, useIntl } from "react-intl";

import {
  Activity,
  Company,
  Deal,
  EntityType,
  EntityTypesSupportedByActivities,
  Person,
} from "@mapmycustomers/shared/types/entity";
import Participant from "@mapmycustomers/shared/types/Participant";
import { stopEvents } from "@mapmycustomers/shared/util/browser";

import AttendeesSection from "@app/component/activity/ActivityAnnotation/AttendeesSection";

import EntityLink from "../EntityLink";

import useEntityClickHandlers from "./ActivityInfoModal/useEntityClickHandlers";
import styles from "./ActivityName.module.scss";
import ActivityTypeIcon from "./ActivityTypeIcon";

interface Props {
  activity: Activity;
  onCompanyClick?: (companyId: Company["id"]) => void;
  onDealClick?: (dealId: Deal["id"]) => void;
  onPersonClick?: (personId: Person["id"]) => void;
}

const ActivityName: React.FC<Props> = ({
  activity,
  onCompanyClick,
  onDealClick,
  onPersonClick,
}) => {
  const intl = useIntl();
  const participants: Participant[] | undefined = activity.participants;

  const [handleCompanyClick, handlePersonClick, handleDealClick] = useEntityClickHandlers(
    activity,
    onCompanyClick,
    onPersonClick,
    onDealClick
  );
  const relatedEntities = [
    ...(activity.account
      ? [{ ...activity.account, entityType: EntityType.COMPANY, onClick: handleCompanyClick }]
      : []),
    ...(activity.contact
      ? [{ ...activity.contact, entityType: EntityType.PERSON, onClick: handlePersonClick }]
      : []),
    ...(activity.deal
      ? [{ ...activity.deal, entityType: EntityType.DEAL, onClick: handleDealClick }]
      : []),
  ];
  const firstAvailableEntity = relatedEntities[0];

  return (
    <div className={styles.container}>
      <div className={styles.iconContainer}>
        <ActivityTypeIcon
          activityType={activity.crmActivityType}
          isAutoEmail={!!activity.emailLog}
        />
      </div>
      <div className={styles.details}>
        <span className={styles.activityName}>
          {activity.name && activity.name.trim().length ? (
            activity.name
          ) : (
            <i>
              {intl.formatMessage({
                id: "home.cards.todayActivitiesForReps.activityList.column.activity.untitled",
                defaultMessage: "Untitled",
                description:
                  "Activity name value on a Today's Activities card for Reps when there's no name",
              })}
            </i>
          )}
        </span>
        <div className={styles.relatedEntitiesContainer}>
          {firstAvailableEntity && (
            <EntityLink
              className={styles.associatedEntityLink}
              entityId={firstAvailableEntity.id}
              entityType={firstAvailableEntity.entityType as EntityTypesSupportedByActivities}
              legacy={false}
            >
              <span className={styles.associatedEntityTitle} onClick={firstAvailableEntity.onClick}>
                {firstAvailableEntity.name}
              </span>
            </EntityLink>
          )}
          {relatedEntities.length > 1 && (
            <Popover
              content={
                <div className={styles.moreContainer}>
                  {relatedEntities.map((item, key) => (
                    <div
                      className={styles.entityLink}
                      key={key}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <EntityLink
                        entityId={item.id}
                        entityType={item.entityType as EntityTypesSupportedByActivities}
                      >
                        <span onClick={item.onClick}>{item.name}</span>
                      </EntityLink>
                    </div>
                  ))}
                </div>
              }
              overlayClassName={styles.overlay}
              placement="bottomLeft"
              title={
                <h3 className={styles.moreTitle}>
                  <FormattedMessage
                    id="activity.activityCard.relatedEntitiesMore.title"
                    defaultMessage="All relationships"
                    description="message for more related entities title"
                  />
                </h3>
              }
            >
              <div className={styles.moreButton}>
                <FormattedMessage
                  id="activity.activityCard.relatedEntitiesMore.button"
                  defaultMessage="+{count} more"
                  description="message for more related entities button"
                  values={{
                    count: relatedEntities.length - 1,
                  }}
                />
              </div>
            </Popover>
          )}
        </div>
        {participants && participants.length > 0 && (
          <div className={styles.participants} onClick={stopEvents}>
            <FontAwesomeIcon className={styles.participantsIcon} icon={faUsers} />
            <Popover
              content={<AttendeesSection activity={activity} className={styles.attendeesSection} />}
              placement="bottom"
              trigger={["click"]}
            >
              <Tooltip
                placement="bottom"
                title={intl.formatMessage({
                  id: "activity.activityCard.attendees.labelTooltip",
                  defaultMessage: "Attendee details",
                  description: "message for attendees label tooltip",
                })}
              >
                <div className={styles.participantsLabel}>
                  <FormattedMessage
                    id="activity.activityCard.attendees.label"
                    defaultMessage="{count} {count, plural, one {attendee} other {attendees}}"
                    description="message for attendees label"
                    values={{
                      count: participants.length,
                    }}
                  />
                </div>
              </Tooltip>
            </Popover>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityName;
